/**
 * Event Manager Module
 * Handles event markers, filtering, and display on the map
 */

import { fetchEventsForCountry } from '../config/firebase';
import { selectedLanguage } from '../utils/translation';
import { getMarkers, getAllEventsLayers } from './mapConfig';
import { showLoader, hideLoader, setLoaderMessage } from './loadingIndicator';

// Data source now uses Firestore; keeping the signature prevents breaking existing imports.
export function setDataLink() {}

const EVENT_TYPE_ALIASES = {
    'all': 'all',
    'shelling/artillery/missile attack': 'shelling_artillery_missile_attack',
    'shelling artillery missile attack': 'shelling_artillery_missile_attack',
    'shelling_artillery_missile_attack': 'shelling_artillery_missile_attack',
    'air/drone strike': 'airstrike',
    'airstrike/drone strike': 'airstrike',
    'airstrike': 'airstrike',
    'air strike': 'airstrike',
    'drone strike': 'drone_attack',
    'drone_attack': 'drone_attack',
    'armed clash': 'armed_clash',
    'armed clashes': 'armed_clash',
    'attack': 'attack',
    'remote explosive/landmine/ied': 'remote_explosive_landmine_ied',
    'remote explosive': 'remote_explosive_landmine_ied',
    'explosions': 'remote_explosive_landmine_ied',
    'abduction/forced disappearance': 'abduction_forced_disappearance',
    'kidnappings': 'abduction_forced_disappearance',
    'arrests/detentions': 'arrests_detentions',
    'arrests': 'arrests_detentions',
    'detentions': 'arrests_detentions',
    'protests/demontrations': 'protests_demonstrations',
    'protests/demonstrations': 'protests_demonstrations',
    'protests': 'protests_demonstrations',
    'chemical attack': 'chemical_attack',
    'chemical attacks': 'chemical_attack',
    'assassination/targeted killing': 'assassination_targeted_killing',
    'assassinations / targeted killings': 'assassination_targeted_killing',
    'cyber attack': 'cyber_attack',
    'cyberattacks': 'cyber_attack',
    'violence against civilians': 'abduction_forced_disappearance',
};

let cachedEvents = [];
let cachedCountryKey = null;

const ICON_BASE_PATH = '/assets/icons/';

function getIconPath(filename) {
    return `${ICON_BASE_PATH}${filename}`;
}

function deriveNormalizedCountry(record) {
    const candidates = [
        record?.country_normalized,
        record?.country,
        record?.geolocation?.country_normalized,
        record?.geolocation?.country,
    ];

    for (const value of candidates) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim().toLowerCase();
        }
    }

    return '';
}

function deriveCountryLabel(record) {
    const candidates = [
        record?.country,
        record?.geolocation?.country,
    ];

    for (const value of candidates) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return '';
}

function formatDate(dateInput) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const day = String(date.getUTCDate()).padStart(2, '0');
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return `${day} ${month} ${year}`;
}

function normalizeCountry(countryName) {
    return typeof countryName === 'string' ? countryName.trim() : '';
}

function normalizeEventTypeValue(value) {
    if (!value) {
        return '';
    }

    const trimmed = value.toString().trim();
    const lower = trimmed.toLowerCase();

    if (Object.prototype.hasOwnProperty.call(EVENT_TYPE_ALIASES, lower)) {
        return EVENT_TYPE_ALIASES[lower];
    }

    return trimmed.replace(/\s+/g, '_').toLowerCase();
}

function deriveEventType(record) {
    const labelCandidates = [
        record?.event_type_label,
        record?.event_type,
        record?.sub_event_type,
        record?.eventType,
    ];

    let label = '';
    for (const value of labelCandidates) {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed) {
                label = trimmed;
                break;
            }
        }
    }

    let normalized = label ? normalizeEventTypeValue(label) : '';

    if (!normalized) {
        const normalizedCandidates = [
            record?.event_type_normalized,
            record?.sub_event_type_normalized,
            record?.sub_event_type,
            record?.event_type,
            record?.eventType,
        ];

        for (const value of normalizedCandidates) {
            if (typeof value === 'string' && value.trim()) {
                normalized = normalizeEventTypeValue(value);
                if (normalized && !label) {
                    label = value.trim();
                }
                if (normalized) {
                    break;
                }
            }
        }
    }

    return {
        label: label || null,
        normalized: normalized || null,
    };
}

function parseEventDate(value) {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (value && typeof value.toDate === 'function') {
        const parsed = value.toDate();
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (value && typeof value.seconds === 'number') {
        const parsed = new Date(value.seconds * 1000);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
}

function toISODate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null;
    }

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function normalizeEvents(records) {
    if (!Array.isArray(records)) {
        return [];
    }

    const normalized = [];

    for (const record of records) {
        const geolocation = record?.geolocation || {};
        const latitude = parseFloat(geolocation.latitude ?? record?.latitude);
        const longitude = parseFloat(geolocation.longitude ?? record?.longitude);

        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
            continue;
        }

        const eventDateObj = parseEventDate(record?.event_date ?? record?.eventDate);
        const isoEventDate = toISODate(eventDateObj);
        const { label: eventTypeLabel, normalized: normalizedEventType } = deriveEventType(record);
        const countryNormalized = deriveNormalizedCountry(record);
        const countryLabel = deriveCountryLabel(record);

        normalized.push({
            id: record?.id || record?.docId || null,
            notes: record?.notes ?? record?.notes_en ?? record?.rephrase_english ?? record?.title ?? '',
            notes_ar: record?.notes_ar ?? record?.rephrase_arabic ?? '',
            notes_tr: record?.notes_tr ?? record?.rephrase_turkish ?? '',
            event_date: isoEventDate ? formatDate(isoEventDate) : '',
            iso_event_date: isoEventDate,
            event_type: eventTypeLabel || normalizedEventType || null,
            event_type_normalized: normalizedEventType || (eventTypeLabel ? normalizeEventTypeValue(eventTypeLabel) : null),
            latitude,
            longitude,
            country: countryLabel || null,
            country_normalized: countryNormalized || null,
        });
    }

    return normalized;
}

function normalizeDateInput(value) {
    if (!value) {
        return null;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : toISODate(parsed);
    }

    if (value instanceof Date) {
        return toISODate(value);
    }

    if (typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : toISODate(parsed);
    }

    return null;
}

function createCustomMarker(lat, lon, popupContent, eventType) {
    let imageUrl;

    switch (eventType) {
        case 'shelling_artillery_missile_attack':
            imageUrl = getIconPath('shelling.png');
            break;
        case 'airstrike':
            imageUrl = getIconPath('airstrike_attack.png');
            break;
        case 'armed_clash':
            imageUrl = getIconPath('armed_clashes.png');
            break;
        case 'attack':
            imageUrl = getIconPath('military_operation.png');
            break;
        case 'remote_explosive_landmine_ied':
            imageUrl = getIconPath('rocket_attack.png');
            break;
        case 'abduction_forced_disappearance':
            imageUrl = getIconPath('violence_against_civilian.png');
            break;
        case 'protests_demonstrations':
            imageUrl = getIconPath('protests.png');
            break;
        case 'drone_attack':
            imageUrl = getIconPath('drone_attack.png');
            break;
        case 'chemical_attack':
            imageUrl = getIconPath('chemical_attack.png');
            break;
        case 'arrests_detentions':
            imageUrl = getIconPath('violence_against_civilian.png');
            break;
        case 'assassination_targeted_killing':
            imageUrl = getIconPath('military_operation.png');
            break;
        case 'cyber_attack':
            imageUrl = getIconPath('drone_attack.png');
            break;
        default:
            imageUrl = getIconPath('protests.png');
    }

    const customIcon = L.icon({
        iconUrl: imageUrl,
        iconSize: [40, 40],
        popupAnchor: [0, -15],
    });

    const marker = L.marker([lat, lon], {
        tags: [eventType],
        icon: customIcon,
    });

    marker.bindPopup(popupContent);
    return marker;
}

function filterEventsByType(events, eventType) {
    if (!eventType || eventType === 'all') {
        return events;
    }

    return events.filter(event => {
        if (!eventType) {
            return true;
        }
        const normalized = event.event_type_normalized || normalizeEventTypeValue(event.event_type);
        return normalized === eventType;
    });
}

function filterEventsByDateRange(events, startDate, endDate) {
    const normalizedStart = normalizeDateInput(startDate);
    const normalizedEnd = normalizeDateInput(endDate);

    return events.filter(event => {
        if (!event.iso_event_date) {
            return false;
        }

        if (normalizedStart && event.iso_event_date < normalizedStart) {
            return false;
        }

        if (normalizedEnd && event.iso_event_date > normalizedEnd) {
            return false;
        }

        return true;
    });
}

function addMarkers(data) {
    const markers = getMarkers();
    const allEventsLayers = getAllEventsLayers();

    if (!markers || !allEventsLayers) {
        console.error('Markers or allEventsLayers not initialized!');
        return;
    }

    if (!data || data.length === 0) {
        console.log('No events available for the current selection.');
        markers.clearLayers();
        return;
    }

    markers.clearLayers();

    data.forEach(d => {
        if (Number.isNaN(d.latitude) || Number.isNaN(d.longitude)) {
            return;
        }

        const languageMap = {
            en: d.notes,
            ar: d.notes_ar,
            tr: d.notes_tr,
        };
        const content = languageMap[selectedLanguage] || d.notes || '';
        const isRTL = selectedLanguage === 'ar';
        const dateTranslation = {
            tr: 'Tarih:',
            en: 'Date:',
            ar: 'التاريخ:',
        };

        const popupContent = `
            <div class="custom-popup ${isRTL ? 'rtl' : ''}">
                <h6>${content}</h6>
                <p><strong>${dateTranslation[selectedLanguage]}</strong> ${d.event_date || ''}</p>
            </div>
        `;

        const markerEventType = d.event_type_normalized || normalizeEventTypeValue(d.event_type);
        const marker = createCustomMarker(
            d.latitude,
            d.longitude,
            popupContent,
            markerEventType || ''
        );
        markers.addLayer(marker);
    });

    allEventsLayers.addLayer(markers);
    console.log(`Added ${data.length} markers to map`);
}

async function fetchEventsFromBackend(countryName, startDate, endDate) {
    const trimmedCountry = normalizeCountry(countryName);

    if (!trimmedCountry) {
        return [];
    }

    const events = await fetchEventsForCountry({
        country: trimmedCountry,
        startDate,
        endDate,
    });

    return normalizeEvents(events);
}

export async function fetchAllCSVData(countryName) {
    const trimmedCountry = normalizeCountry(countryName);

    if (!trimmedCountry) {
        console.warn('fetchAllCSVData called without a valid country name');
        return;
    }

    const displayCountry = trimmedCountry;
    setLoaderMessage(`Loading events for ${displayCountry}…`);
    showLoader();

    try {
        const normalizedCountryKey = trimmedCountry.toLowerCase();
        const events = await fetchEventsFromBackend(trimmedCountry);
        cachedCountryKey = normalizedCountryKey;
        cachedEvents = events;
        addMarkers(events);
        console.log("All events: ", events);
        return events;
    } catch (error) {
        console.error('Error fetching recent events:', error);
        throw error;
    } finally {
        hideLoader();
    }
}

export async function fetchCSVDataWithEventTypeAndDate(countryName, startDate, endDate, eventType) {
    const trimmedCountry = normalizeCountry(countryName);

    if (!trimmedCountry) {
        console.warn('No country selected for filtering.');
        return;
    }

    const normalizedCountryKey = trimmedCountry.toLowerCase();
    const normalizedEventType = normalizeEventTypeValue(eventType);
    const hasDateRange = Boolean(startDate) || Boolean(endDate);
    let events = [];

    let displayedLoader = false;

    try {
        if (hasDateRange) {
            setLoaderMessage('Applying filters…');
            showLoader();
            displayedLoader = true;
            events = await fetchEventsFromBackend(trimmedCountry, startDate, endDate);
            cachedCountryKey = normalizedCountryKey;
            cachedEvents = events;
        } else if (cachedCountryKey === normalizedCountryKey && cachedEvents.length) {
            events = cachedEvents;
        } else {
            setLoaderMessage(`Loading events for ${trimmedCountry}…`);
            showLoader();
            displayedLoader = true;
            events = await fetchEventsFromBackend(trimmedCountry);
            cachedCountryKey = normalizedCountryKey;
            cachedEvents = events;
        }
    } catch (error) {
        console.error('Error fetching events for filtering:', error);
        return;
    } finally {
        if (displayedLoader) {
            hideLoader();
        }
    }

    const typeFiltered = filterEventsByType(events, normalizedEventType);
    const finalEvents = hasDateRange
        ? filterEventsByDateRange(typeFiltered, startDate, endDate)
        : typeFiltered;

    addMarkers(finalEvents);
    return finalEvents;
}

